import React, { useRef, useState } from "react";
import Dialog from "../../Components/Dialog";
import TextField from "../../Components/TextField";
import Button from "../../Components/Button";
import { Typography } from "../../Components/Typography";
import { useAuth } from "../../Context/authContext";
import { inviteTeamMember, checkUserExists } from "../../Utils/apiServices";
import { stripNumbersFromName } from "../../Utils/nameValidation";

// Collect just the teammate's name + email. The invitee receives an email with a
// link to set up their own account; once they complete it they become a
// SECONDARY_STUDENT (view-only collaborator) on the idea.
const getEmptyForm = () => ({
  first_name: "",
  last_name: "",
  email: "",
});

/**
 * Two modes:
 * - With `ideaId`: sends the invitation immediately (Collaboration tab on an
 *   existing idea).
 * - With `onCollect` (no ideaId): hands the validated invitee back to the parent
 *   — used on the idea creation form, where invites are sent after the idea is
 *   submitted. `existingMembers` lets the parent catch duplicates locally.
 */
const AddTeamMemberDialog = ({ isOpen, onClose, ideaId, onAdded, onCollect, existingMembers = [] }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState(getEmptyForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [emailExistsError, setEmailExistsError] = useState("");
  const latestEmailRef = useRef("");

  const handleInputChange = (field, value) => {
    if (field === "first_name" || field === "last_name") {
      value = stripNumbersFromName(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
    if (field === "email") {
      latestEmailRef.current = value;
      setEmailExistsError("");
    }
  };

  // Warn early if the email already belongs to a registered user — they should
  // sign in instead, and the backend rejects inviting an existing account.
  const handleEmailBlur = () => {
    const valueToCheck = formData.email?.trim?.() || "";
    if (!valueToCheck) {
      setEmailExistsError("");
      return;
    }
    checkUserExists(valueToCheck)
      .then((res) => {
        if (latestEmailRef.current !== valueToCheck) return;
        const exists = res?.data?.data?.exists;
        const field = res?.data?.data?.field;
        if (exists && field === "email") {
          setEmailExistsError("This email is already registered. Ask them to sign in instead.");
        } else {
          setEmailExistsError("");
        }
      })
      .catch(() => {
        if (latestEmailRef.current === valueToCheck) setEmailExistsError("");
      });
  };

  const validate = () => {
    const email = formData.email?.trim?.() || "";

    if (!formData.first_name || !formData.last_name) {
      setError("First name and last name are required");
      return false;
    }
    if (formData.first_name.trim().length < 2) {
      setError("First name must be at least 2 characters");
      return false;
    }
    if (formData.last_name.trim().length < 2) {
      setError("Last name must be at least 2 characters");
      return false;
    }
    if (!email) {
      setError("Email is required");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (emailExistsError) {
      setError(emailExistsError);
      return false;
    }
    // Duplicates among invitees already collected by the parent (creation form).
    if (existingMembers.some((m) => (m.email || "").toLowerCase() === email.toLowerCase())) {
      setError("A team member with this email has already been added.");
      return false;
    }
    return true;
  };

  const resetAndClose = () => {
    setFormData(getEmptyForm());
    setError("");
    setEmailExistsError("");
    onClose?.();
  };

  const handleClose = () => {
    if (submitting) return;
    resetAndClose();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;

    const member = {
      email: formData.email.trim().toLowerCase(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
    };

    // Collect-only mode: hand the validated invitee back to the parent.
    if (onCollect) {
      onCollect(member);
      resetAndClose();
      return;
    }

    setSubmitting(true);
    try {
      const res = await inviteTeamMember({
        idea_id: ideaId,
        invited_by: user?.id,
        ...member,
      });

      if (!res?.success) {
        setError(res?.error || "Unable to send the invitation. Please try again.");
        return;
      }

      onAdded?.();
      resetAndClose();
    } catch (err) {
      setError(err?.error || "An error occurred while sending the invitation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Team Member"
      description={
        onCollect
          ? "Enter your team member's name and email. We'll email them an invite to join when you submit the idea — they'll set up their own account with view-only access."
          : "Enter your team member's name and email. We'll email them an invite to set up their own account and join this idea with view-only access."
      }
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6 overflow-y-auto text-left">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label="First Name"
            name="team_member_first_name"
            required
            value={formData.first_name}
            onChange={(e) => handleInputChange("first_name", e.target.value)}
            placeholder="First name"
          />
          <TextField
            label="Last Name"
            name="team_member_last_name"
            required
            value={formData.last_name}
            onChange={(e) => handleInputChange("last_name", e.target.value)}
            placeholder="Last name"
          />
        </div>
        <TextField
          label="Email"
          name="team_member_email"
          type="email"
          required
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          onBlur={handleEmailBlur}
          placeholder="Enter their email"
          error={Boolean(emailExistsError)}
          helperText={emailExistsError || "We'll send the invitation to this address."}
        />

        {error && (
          <Typography as="p" className="text-sm text-red-600">
            {error}
          </Typography>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Sending..." : onCollect ? "Add Team Member" : "Send Invite"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default AddTeamMemberDialog;
